import type * as React from 'react';
import { cva } from 'class-variance-authority';
import { Sparkles } from 'lucide-react';
import { cn } from '../../lib/cn.ts';

/** Whose turn it is: the reader's own words sit on the right. */
export type TranscriptSide = 'self' | 'other';

export type TranscriptProps = Omit<React.ComponentProps<'div'>, 'style'>;

/**
 * A finished conversation read back: who said what, with remarks on some of it.
 *
 * Not the chat: nothing arrives here any more and nobody is typing, so every
 * turn is named above its bubble rather than told apart by an avatar.
 */
export function Transcript({ className, ...props }: TranscriptProps) {
  return (
    <div data-slot='transcript' className={cn('flex flex-col', className)} {...props} />
  );
}

const transcriptBubbleVariants = cva(
  'max-w-[80%] whitespace-pre-line rounded-lg px-3.75 py-2.75 text-md leading-relaxed',
  {
    variants: {
      side: {
        self: 'rounded-br-xs bg-fill text-ink',
        other: 'rounded-bl-xs border border-line bg-surface text-ink-body',
      },
    },
  },
);

export interface TranscriptMessageProps extends Omit<
  React.ComponentProps<'div'>,
  'style'
> {
  side: TranscriptSide;
  /** Who said it, shown above the bubble. */
  speaker: string;
}

/** One turn: the speaker's name, and what they said under it. */
export function TranscriptMessage({
  side,
  speaker,
  className,
  children,
  ...props
}: TranscriptMessageProps) {
  return (
    <div
      data-slot='transcript-message'
      data-side={side}
      className={cn(
        'mt-3.5 flex flex-col first:mt-0',
        side === 'self' ? 'items-end' : 'items-start',
        className,
      )}
      {...props}
    >
      <p className='mb-1.25 text-sm text-ink-muted'>{speaker}</p>
      <div className={transcriptBubbleVariants({ side })}>{children}</div>
    </div>
  );
}

export interface TranscriptRemarkProps extends Omit<
  React.ComponentProps<'div'>,
  'style'
> {
  /** The side of the message it is about, which it lines up under. */
  side: TranscriptSide;
  /** Who is remarking, next to the sparkles. */
  author: string;
  /** What could have been said instead, already in its quotation marks. */
  betterPhrasing?: React.ReactNode;
}

/**
 * A remark on the message just above it. Green, because it is Wundo speaking
 * and not one of the two people in the conversation.
 */
export function TranscriptRemark({
  side,
  author,
  betterPhrasing,
  className,
  children,
  ...props
}: TranscriptRemarkProps) {
  return (
    <div
      data-slot='transcript-remark'
      data-side={side}
      className={cn(
        'mt-2 max-w-[80%] rounded-md border border-accent-line bg-accent-tint-faint px-3.5 py-2.75 text-base leading-relaxed text-ink',
        side === 'self' ? 'self-end' : 'self-start',
        className,
      )}
      {...props}
    >
      <p className='mb-1 flex items-center gap-1.5 text-sm font-semibold text-success'>
        <Sparkles aria-hidden='true' className='size-3.5' />
        {author}
      </p>
      <p>{children}</p>
      {betterPhrasing ? (
        <p className='mt-1.5 italic text-ink-body'>{betterPhrasing}</p>
      ) : null}
    </div>
  );
}

export type TranscriptNoteProps = Omit<React.ComponentProps<'p'>, 'style'>;

/** The small print under the last turn. */
export function TranscriptNote({ className, ...props }: TranscriptNoteProps) {
  return (
    <p
      data-slot='transcript-note'
      className={cn('mt-4.5 text-center text-sm italic text-ink-muted', className)}
      {...props}
    />
  );
}
